/**
 * Patient identity helpers built on the mobile number.
 *
 * The same patient is often stored under more than one record (a quick entry
 * and a full registration, or a re-registration with a slightly different
 * spelling of the name), so name-based matching misses them. The mobile number
 * is the identity the counter actually verifies at the desk — every lookup that
 * needs "is this the same patient?" must key off this helper instead of the
 * name.
 */

/**
 * Canonical, formatting-free form of a mobile number.
 *
 * - strips spaces, dashes, parentheses and a leading `+`
 * - drops a leading country code (`880` for Bangladesh)
 * - drops a single leading trunk `0`
 *
 * This makes `+880 1712-345678`, `8801712345678`, `01712345678` and
 * `1712345678` all compare equal. Numbers that do not follow that shape are
 * returned as their plain digits so exact-digit comparison still works.
 */
export function normalizeMobile(value?: string | null): string {
  let digits = String(value ?? '').replace(/\D+/g, '');
  if (!digits) return '';
  if (digits.startsWith('880') && digits.length > 10) digits = digits.slice(3);
  if (digits.startsWith('0')) digits = digits.slice(1);
  return digits;
}

/** True when both numbers point at the same mobile line. */
export function isSameMobile(a?: string | null, b?: string | null): boolean {
  const left = normalizeMobile(a);
  return left !== '' && left === normalizeMobile(b);
}

/**
 * Mobile number carried by a receipt's embedded patient. The API returns
 * `mobileNumber`; older payloads used `mobile`, so both are accepted.
 */
export function receiptPatientMobile(patient?: {
  mobileNumber?: string | null;
  mobile?: string | null;
} | null): string {
  return patient?.mobileNumber || patient?.mobile || '';
}
